/**
 * Fuse.js Search Utilities
 * Shared configuration and helpers for search-overlay.js and search-page.js
 */
window.AylaTheme = window.AylaTheme || {};

window.AylaTheme.fuseSearch = (() => {
  'use strict';

  // Fuse.js configuration optimized for Polish product names
  const FUSE_OPTIONS = {
    // Which keys to search in
    keys: [
      { name: 'title', weight: 0.5 },
      { name: 'product_type', weight: 0.2 },
      { name: 'vendor', weight: 0.1 },
      { name: '_searchText', weight: 0.2 }
    ],
    // Matching behavior
    threshold: 0.5,           // 0 = exact, 1 = match anything. 0.5 allows partial matches like "kaczk" → "kaczki".
    distance: 100,            // How far to search for a match
    minMatchCharLength: 2,    // Minimum characters before matching
    ignoreLocation: true,     // Search entire string, not just beginning
    useExtendedSearch: false, // Keep it simple
    includeScore: true,       // We need scores for sorting
    includeMatches: true,     // Useful for highlighting later
    findAllMatches: false,    // Stop at first good match per field
    isCaseSensitive: false,   // Case insensitive
    shouldSort: true          // Let Fuse sort by score
  };

  // Polish synonym mappings for query expansion (includes all declension forms)
  const SYNONYM_MAP = {
    kurczak: ['kurczak', 'kurczaka', 'kurczaki', 'kurczakiem', 'kurczę', 'chicken'],
    kaczka: ['kaczka', 'kaczki', 'kaczką', 'kacze', 'kaczym', 'duck'],
    indyk: ['indyk', 'indyka', 'indycze', 'indyczym', 'turkey'],
    wolowina: ['wołowina', 'wolowina', 'wołowe', 'wołowym', 'beef'],
    losos: ['łosoś', 'losos', 'łososia', 'łososiem', 'salmon'],
    ryba: ['ryba', 'ryby', 'rybą', 'fish'],
    pies: ['pies', 'psa', 'psem', 'dog'],
    kot: ['kot', 'kota', 'kotem', 'cat'],
    przysmak: ['przysmak', 'przysmaki', 'przysmakiem', 'treats'],
    liofilizowane: ['liofilizowane', 'liofilizowany', 'liofilizowanym', 'freeze-dried'],
    mieszane: ['mieszane', 'mieszany', 'mieszanym', 'mieszanka', 'blend', 'mix', 'miks', 'mixed']
  };

  /**
   * Remove Polish diacritics for normalization
   */
  const removeDiacritics = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ł/g, 'l')
    .replace(/Ł/g, 'L');

  /**
   * Normalize a query string for comparison
   */
  const normalizeQuery = (value) => removeDiacritics(String(value || '').toLowerCase().trim());

  /**
   * Expand query with synonyms
   * Returns array of query terms to search for
   */
  const expandQueryWithSynonyms = (query) => {
    const normalized = normalizeQuery(query);
    const terms = new Set([query.trim(), normalized]);

    const tokens = normalized.split(/\s+/).filter(Boolean);
    tokens.forEach((token) => {
      // Check each synonym group
      Object.entries(SYNONYM_MAP).forEach(([key, synonyms]) => {
        const normalizedSynonyms = synonyms.map(normalizeQuery);
        // Check exact match OR prefix match (for partial typing like "kaczk" → "kaczka")
        const hasMatch = normalizedSynonyms.some((syn) =>
          syn === token || syn.startsWith(token) || token.startsWith(syn)
        ) || key === token || key.startsWith(token) || token.startsWith(key);
        if (hasMatch) {
          synonyms.forEach((syn) => terms.add(syn));
        }
      });
    });

    return Array.from(terms);
  };

  /**
   * Create a Fuse instance for product searching
   */
  const createProductIndex = (products) => {
    if (typeof Fuse === 'undefined') {
      console.warn('Fuse.js not loaded, falling back to basic search');
      return null;
    }
    return new Fuse(products, FUSE_OPTIONS);
  };

  /**
   * Search products using Fuse.js with synonym expansion
   * Returns sorted results with scores
   */
  const searchProducts = (fuseIndex, query, limit = 10) => {
    if (!fuseIndex || !query) return [];

    const expandedTerms = expandQueryWithSynonyms(query);
    const allResults = new Map();

    // Search for each expanded term and merge results
    expandedTerms.forEach((term) => {
      const results = fuseIndex.search(term, { limit: limit * 2 });
      results.forEach((result) => {
        const key = result.item.handle || result.item.url || result.item.title;
        const existing = allResults.get(key);
        // Keep the better score (lower is better in Fuse.js)
        if (!existing || result.score < existing.score) {
          allResults.set(key, result);
        }
      });
    });

    // Sort by score and return items
    return Array.from(allResults.values())
      .sort((a, b) => a.score - b.score)
      .slice(0, limit)
      .map((result) => ({
        ...result.item,
        _fuseScore: result.score,
        _fuseMatches: result.matches
      }));
  };

  /**
   * Re-score products from Shopify API using Fuse.js matching
   * This ensures consistent scoring across all search sources
   */
  const rescoreProducts = (products, query, limit = 10) => {
    if (!products || !products.length) return [];
    if (typeof Fuse === 'undefined') return products.slice(0, limit);

    const fuseIndex = createProductIndex(products);
    return searchProducts(fuseIndex, query, limit);
  };

  /**
   * Check if a product is a strong match for query
   * Used to filter out weak substring matches
   */
  const isStrongMatch = (product, query, threshold = 0.4) => {
    if (typeof Fuse === 'undefined') return true; // Fail open

    const fuseIndex = createProductIndex([product]);
    const results = fuseIndex.search(query);

    if (!results.length) return false;
    return results[0].score <= threshold;
  };

  /**
   * Position-aware product ranking.
   *
   * Fuse.js (with ignoreLocation:true) treats a match at the start of a word
   * the same as one buried inside another word.  This causes "ENT" to score
   * "Entenherzen" and "Essentielle" nearly identically.
   *
   * rankProducts applies a tiered scoring system that heavily favours
   * word-prefix matches in the title, so duck products sort before turkey
   * when the user types "ENT".
   */
  const rankProducts = (products, query, limit = 10) => {
    if (!products || !products.length) return [];

    const normalizedQ = normalizeQuery(query);
    if (!normalizedQ) return products.slice(0, limit);

    const queryTokens = normalizedQ.split(/\s+/).filter(Boolean);

    // Build synonym set for Polish word-form matching (kaczka↔kacze, indyk↔indycze, etc.)
    const synonymSet = new Set();
    queryTokens.forEach((token) => {
      Object.entries(SYNONYM_MAP).forEach(([key, synonyms]) => {
        const nKey = normalizeQuery(key);
        const nSynonyms = synonyms.map(normalizeQuery);
        const isMatch = nKey === token || nKey.startsWith(token) || token.startsWith(nKey) ||
          nSynonyms.some((s) => s === token || s.startsWith(token) || token.startsWith(s));
        if (isMatch) {
          nSynonyms.forEach((s) => { if (s) synonymSet.add(s); });
        }
      });
    });
    // Remove original query tokens – they get higher-tier scoring
    queryTokens.forEach((t) => synonymSet.delete(t));

    const WORD_SPLIT = /[\s|,.\-:;/()]+/;

    const scoreOne = (product) => {
      let score = 0;
      const titleNorm = normalizeQuery(product.title || '');
      const titleWords = titleNorm.split(WORD_SPLIT).filter(Boolean);

      // --- Title scoring (highest signal) ---
      if (titleNorm === normalizedQ) {
        score += 200;
      } else if (titleNorm.startsWith(normalizedQ)) {
        score += 180;
      } else {
        let best = 0;
        for (const w of titleWords) {
          if (w === normalizedQ) { best = Math.max(best, 150); }
          else if (w.startsWith(normalizedQ)) { best = Math.max(best, 120); }
          else if (normalizedQ.startsWith(w)) { best = Math.max(best, 60); }
          else if (w.includes(normalizedQ)) { best = Math.max(best, 20); }
        }

        // Synonym word-form matching (lower tier than direct matches).
        // Catches Polish declensions: kaczka→kacze, indyk→indycze, etc.
        if (best === 0 && synonymSet.size > 0) {
          for (const w of titleWords) {
            if (synonymSet.has(w)) { best = Math.max(best, 80); }
            else {
              for (const syn of synonymSet) {
                if (w.startsWith(syn) || syn.startsWith(w)) {
                  best = Math.max(best, 50);
                }
              }
            }
            if (best >= 80) break;
          }
        }

        score += best;
      }

      // --- Multi-token bonus ---
      if (queryTokens.length > 1) {
        let matched = 0;
        for (const t of queryTokens) {
          if (titleWords.some((w) => w === t || w.startsWith(t))) matched++;
        }
        score += matched * 10;
      }

      // --- Product type ---
      const typeNorm = normalizeQuery(product.product_type || '');
      if (typeNorm) {
        const typeWords = typeNorm.split(WORD_SPLIT).filter(Boolean);
        for (const w of typeWords) {
          if (w === normalizedQ || w.startsWith(normalizedQ)) { score += 25; break; }
          if (synonymSet.has(w)) { score += 15; break; }
        }
      }

      // --- Tags ---
      const rawTags = product.tags;
      if (rawTags) {
        const tagStr = normalizeQuery(Array.isArray(rawTags) ? rawTags.join(' ') : String(rawTags));
        const tagWords = tagStr.split(WORD_SPLIT).filter(Boolean);
        for (const w of tagWords) {
          if (w === normalizedQ || w.startsWith(normalizedQ)) { score += 15; break; }
          if (synonymSet.has(w)) { score += 10; break; }
        }
      }

      // --- Search text fallback (body, vendor, etc.) ---
      if (score <= 0) {
        const searchText = product._searchText || '';
        if (searchText) {
          const words = searchText.split(WORD_SPLIT).filter(Boolean);
          for (const w of words) {
            if (w === normalizedQ || w.startsWith(normalizedQ)) { score += 8; break; }
          }
          if (score <= 0 && searchText.includes(normalizedQ)) {
            score += 2;
          }
          if (score <= 0 && synonymSet.size > 0) {
            for (const w of words) {
              if (synonymSet.has(w)) { score += 5; break; }
            }
          }
        }
      }

      // --- Availability ---
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const isAvailable = variants.some((v) => v && v.available);
      score += isAvailable ? 5 : -10;

      return score;
    };

    return products
      .map((product) => ({ product, score: scoreOne(product) }))
      .filter((e) => e.score > 0)
      .sort((a, b) => b.score - a.score || String(a.product.title || '').localeCompare(String(b.product.title || '')))
      .slice(0, limit)
      .map((e) => e.product);
  };

  // Public API
  return {
    FUSE_OPTIONS,
    SYNONYM_MAP,
    removeDiacritics,
    normalizeQuery,
    expandQueryWithSynonyms,
    createProductIndex,
    searchProducts,
    rescoreProducts,
    isStrongMatch,
    rankProducts
  };
})();
